import React, { useState, useMemo } from 'react';
import { 
  ArrowLeft, Search, Plus, ChevronRight, TrendingUp, TrendingDown,
  Wallet, Building2, CreditCard, Receipt, Banknote, PiggyBank,
  FileText, Calculator
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const ChartOfAccounts = ({ onBack }) => {
  const [activeTab, setActiveTab] = useState('accounts');
  const [searchTerm, setSearchTerm] = useState('');

  // Chart of Accounts data
  const accounts = useMemo(() => [
    { id: 1, code: '1000', name: 'Cash', type: 'Asset', balance: 12450.00, icon: Banknote },
    { id: 2, code: '1100', name: 'Bank Account - Current', type: 'Asset', balance: 45230.50, icon: Building2 },
    { id: 3, code: '1200', name: 'Accounts Receivable', type: 'Asset', balance: 3272.56, icon: Receipt },
    { id: 4, code: '1300', name: 'Inventory', type: 'Asset', balance: 850.00, icon: FileText },
    { id: 5, code: '2000', name: 'Accounts Payable', type: 'Liability', balance: -1250.00, icon: CreditCard },
    { id: 6, code: '2100', name: 'Credit Card', type: 'Liability', balance: -425.30, icon: CreditCard },
    { id: 7, code: '2200', name: 'VAT Payable', type: 'Liability', balance: -1847.20, icon: Calculator },
    { id: 8, code: '3000', name: 'Owner\'s Equity', type: 'Equity', balance: 25000.00, icon: PiggyBank },
    { id: 9, code: '3100', name: 'Retained Earnings', type: 'Equity', balance: 18450.00, icon: TrendingUp },
    { id: 10, code: '4000', name: 'Sales Revenue', type: 'Revenue', balance: 27500.06, icon: TrendingUp },
    { id: 11, code: '4100', name: 'Service Revenue', type: 'Revenue', balance: 17691.86, icon: Wallet },
    { id: 12, code: '5000', name: 'Cost of Goods Sold', type: 'Expense', balance: -2150.00, icon: TrendingDown },
    { id: 13, code: '5100', name: 'Fuel Expenses', type: 'Expense', balance: -601.53, icon: TrendingDown },
    { id: 14, code: '5200', name: 'Equipment Expenses', type: 'Expense', balance: -245.00, icon: TrendingDown },
    { id: 15, code: '5300', name: 'Insurance', type: 'Expense', balance: -125.00, icon: TrendingDown },
  ], []);

  // Balance Sheet data
  const balanceSheet = useMemo(() => ({
    date: '31 Jan 2026',
    assets: {
      current: [
        { name: 'Cash', amount: 12450.00 },
        { name: 'Bank Account', amount: 45230.50 },
        { name: 'Accounts Receivable', amount: 3272.56 },
        { name: 'Inventory', amount: 850.00 },
      ],
      fixed: [
        { name: 'Equipment', amount: 5500.00 },
        { name: 'Vehicles', amount: 12000.00 },
        { name: 'Less: Accumulated Depreciation', amount: -3200.00 },
      ],
    },
    liabilities: {
      current: [
        { name: 'Accounts Payable', amount: 1250.00 },
        { name: 'Credit Card Payable', amount: 425.30 },
        { name: 'VAT Payable', amount: 1847.20 },
        { name: 'Accrued Expenses', amount: 380.00 },
      ],
      longTerm: [
        { name: 'Vehicle Loan', amount: 8500.00 },
      ],
    },
    equity: [
      { name: 'Owner\'s Capital', amount: 25000.00 },
      { name: 'Retained Earnings', amount: 18450.00 },
      { name: 'Current Year Earnings', amount: 20250.56 },
    ],
  }), []);

  // VAT Return data
  const vatReturn = useMemo(() => ({
    period: 'Q4 2025 (Oct - Dec)',
    dueDate: '7 Feb 2026',
    status: 'Due',
    boxes: [
      { box: '1', label: 'VAT due on sales', amount: 5500.00 },
      { box: '2', label: 'VAT due on acquisitions from EC', amount: 0.00 },
      { box: '3', label: 'Total VAT due (1 + 2)', amount: 5500.00 },
      { box: '4', label: 'VAT reclaimed on purchases', amount: 3652.80 },
      { box: '5', label: 'Net VAT to pay (3 - 4)', amount: 1847.20, isHighlight: true },
      { box: '6', label: 'Total sales excluding VAT', amount: 27500.00 },
      { box: '7', label: 'Total purchases excluding VAT', amount: 18264.00 },
      { box: '8', label: 'Total EC supplies', amount: 0.00 },
      { box: '9', label: 'Total EC acquisitions', amount: 0.00 },
    ],
  }), []);

  const tabs = [
    { id: 'accounts', label: 'CHART OF ACCOUNTS' },
    { id: 'balance', label: 'BALANCE SHEET' },
    { id: 'vat', label: 'VAT RETURN' },
  ];

  const filteredAccounts = accounts.filter(acc => 
    acc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    acc.code.includes(searchTerm)
  );

  const getTypeColor = (type) => {
    const colors = {
      'Asset': 'text-emerald-600 bg-blue-100',
      'Liability': 'text-red-600 bg-red-100',
      'Equity': 'text-purple-600 bg-purple-100',
      'Revenue': 'text-green-600 bg-green-100',
      'Expense': 'text-amber-600 bg-amber-100',
    };
    return colors[type] || 'text-gray-600 bg-gray-100';
  };

  const totalAssets = balanceSheet.assets.current.reduce((s, a) => s + a.amount, 0) + 
                      balanceSheet.assets.fixed.reduce((s, a) => s + a.amount, 0);
  const totalLiabilities = balanceSheet.liabilities.current.reduce((s, a) => s + a.amount, 0) + 
                           balanceSheet.liabilities.longTerm.reduce((s, a) => s + a.amount, 0);
  const totalEquity = balanceSheet.equity.reduce((s, a) => s + a.amount, 0);

  const renderContent = () => {
    switch (activeTab) {
      case 'accounts':
        return (
          <div className="space-y-2">
            {/* Search */}
            <div className="p-4 bg-white border-b border-gray-200">
              <div className="relative">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search accounts..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                  data-testid="account-search"
                />
              </div>
            </div>

            {/* Accounts List */}
            <div className="p-4 space-y-2">
              {filteredAccounts.map(account => {
                const Icon = account.icon;
                return (
                  <div 
                    key={account.id}
                    className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200 hover:shadow-md transition-all cursor-pointer"
                    data-testid={`account-${account.code}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getTypeColor(account.type)}`}>
                        <Icon size={20} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-500 font-mono">{account.code}</span>
                          <span className="font-medium text-gray-900">{account.name}</span>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${getTypeColor(account.type)}`}>
                          {account.type}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-lg font-bold ${account.balance >= 0 ? 'text-gray-900' : 'text-red-600'}`}>
                        £{Math.abs(account.balance).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                        {account.balance < 0 && ' CR'}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );

      case 'balance':
        return (
          <div className="p-4 space-y-6">
            <Card>
              <CardHeader className="bg-gray-50">
                <CardTitle className="text-center">
                  <span className="text-lg text-gray-900">Balance Sheet</span>
                  <p className="text-sm text-gray-500 font-normal mt-1">As of {balanceSheet.date}</p>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                {/* Assets */}
                <div className="mb-6">
                  <h3 className="text-lg font-bold text-emerald-600 mb-3 flex items-center gap-2">
                    <TrendingUp size={20} />
                    ASSETS
                  </h3>
                  
                  <div className="ml-4 mb-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Current Assets</h4>
                    {balanceSheet.assets.current.map((item, idx) => (
                      <div key={idx} className="flex justify-between py-1 border-b border-gray-100">
                        <span className="text-gray-600">{item.name}</span>
                        <span className="font-medium">£{item.amount.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
                      </div>
                    ))}
                    <div className="flex justify-between py-2 font-semibold text-emerald-600">
                      <span>Total Current Assets</span>
                      <span>£{balanceSheet.assets.current.reduce((s, a) => s + a.amount, 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
                    </div>
                  </div>

                  <div className="ml-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Fixed Assets</h4>
                    {balanceSheet.assets.fixed.map((item, idx) => (
                      <div key={idx} className="flex justify-between py-1 border-b border-gray-100">
                        <span className="text-gray-600">{item.name}</span>
                        <span className={`font-medium ${item.amount < 0 ? 'text-red-600' : ''}`}>
                          {item.amount < 0 ? '(' : ''}£{Math.abs(item.amount).toLocaleString('en-GB', { minimumFractionDigits: 2 })}{item.amount < 0 ? ')' : ''}
                        </span>
                      </div>
                    ))}
                    <div className="flex justify-between py-2 font-semibold text-emerald-600">
                      <span>Total Fixed Assets</span>
                      <span>£{balanceSheet.assets.fixed.reduce((s, a) => s + a.amount, 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
                    </div>
                  </div>

                  <div className="flex justify-between py-3 mt-2 border-t-2 border-blue-200 font-bold text-emerald-700 text-lg">
                    <span>TOTAL ASSETS</span>
                    <span>£{totalAssets.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
                  </div>
                </div>

                {/* Liabilities */}
                <div className="mb-6">
                  <h3 className="text-lg font-bold text-red-600 mb-3 flex items-center gap-2">
                    <TrendingDown size={20} />
                    LIABILITIES
                  </h3>
                  
                  <div className="ml-4 mb-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Current Liabilities</h4>
                    {balanceSheet.liabilities.current.map((item, idx) => (
                      <div key={idx} className="flex justify-between py-1 border-b border-gray-100">
                        <span className="text-gray-600">{item.name}</span>
                        <span className="font-medium">£{item.amount.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
                      </div>
                    ))}
                  </div>

                  <div className="ml-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Long-term Liabilities</h4>
                    {balanceSheet.liabilities.longTerm.map((item, idx) => (
                      <div key={idx} className="flex justify-between py-1 border-b border-gray-100">
                        <span className="text-gray-600">{item.name}</span>
                        <span className="font-medium">£{item.amount.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
                      </div>
                    ))}
                  </div>

                  <div className="flex justify-between py-3 mt-2 border-t-2 border-red-200 font-bold text-red-700 text-lg">
                    <span>TOTAL LIABILITIES</span>
                    <span>£{totalLiabilities.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
                  </div>
                </div>

                {/* Equity */}
                <div>
                  <h3 className="text-lg font-bold text-purple-600 mb-3 flex items-center gap-2">
                    <PiggyBank size={20} />
                    EQUITY
                  </h3>
                  
                  <div className="ml-4">
                    {balanceSheet.equity.map((item, idx) => (
                      <div key={idx} className="flex justify-between py-1 border-b border-gray-100">
                        <span className="text-gray-600">{item.name}</span>
                        <span className="font-medium">£{item.amount.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
                      </div>
                    ))}
                  </div>

                  <div className="flex justify-between py-3 mt-2 border-t-2 border-purple-200 font-bold text-purple-700 text-lg">
                    <span>TOTAL EQUITY</span>
                    <span>£{totalEquity.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
                  </div>
                </div>

                {/* Total Liabilities + Equity */}
                <div className="flex justify-between py-4 mt-4 border-t-4 border-gray-300 font-bold text-gray-900 text-xl bg-gray-50 px-4 rounded-lg">
                  <span>TOTAL LIABILITIES + EQUITY</span>
                  <span>£{(totalLiabilities + totalEquity).toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case 'vat':
        return (
          <div className="p-4 space-y-4">
            {/* VAT Return Header */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">VAT Return</h3>
                    <p className="text-sm text-gray-500">{vatReturn.period}</p>
                  </div>
                  <div className="text-right">
                    <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium">
                      {vatReturn.status}
                    </span>
                    <p className="text-xs text-gray-500 mt-1">Due: {vatReturn.dueDate}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* VAT Boxes */}
            <Card>
              <CardHeader>
                <CardTitle className="text-zinc-800">VAT Return Boxes</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {vatReturn.boxes.map((box) => (
                  <div 
                    key={box.box}
                    className={`flex items-center justify-between p-4 rounded-lg ${box.isHighlight ? 'bg-pink-50 border-2 border-pink-200' : 'bg-gray-50'}`}
                  >
                    <div className="flex items-center gap-3">
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${box.isHighlight ? 'bg-pink-500 text-white' : 'bg-gray-200 text-gray-600'}`}>
                        {box.box}
                      </span>
                      <span className={`${box.isHighlight ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>
                        {box.label}
                      </span>
                    </div>
                    <span className={`text-lg font-bold ${box.isHighlight ? 'text-pink-600' : 'text-gray-900'}`}>
                      £{box.amount.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Submit Button */}
            <button 
              className="w-full py-4 bg-pink-500 hover:bg-pink-600 text-white font-semibold rounded-lg transition-colors"
              data-testid="submit-vat-return"
            >
              Submit VAT Return to HMRC
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="h-full flex flex-col bg-gray-50" data-testid="chart-of-accounts-page">
      {/* Header */}
      <div className="bg-zinc-800 text-white px-4 py-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            {onBack && (
              <button onClick={onBack} className="p-2 hover:bg-white/10 rounded-lg transition-colors" data-testid="coa-back-btn">
                <ArrowLeft size={20} />
              </button>
            )}
            <h1 className="text-xl font-semibold">Chart Of Accounts</h1>
          </div>
          <button className="p-2 hover:bg-white/10 rounded-lg transition-colors">
            <Plus size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-white/10 rounded-lg p-1">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-2 px-3 rounded-lg text-xs font-medium transition-colors ${activeTab === tab.id ? 'bg-white text-zinc-800' : 'text-white/80 hover:text-white hover:bg-white/10'}`}
              data-testid={`tab-${tab.id}`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {renderContent()}
      </div>
    </div>
  );
};

export default ChartOfAccounts;
